import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { SimpleSelect } from '../../../../shared/layout/forms/AppSelect';
import {
    fetchManufacturerSupportClaims,
    fetchManufacturerSupportStats,
    updateManufacturerSupportClaim,
} from '../../../../services/Http/ManufacturerApi';

const ManufacturerSupport = () => {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);

    useEffect(() => {
        const loadStats = async () => {
            try {
                const response = await fetchManufacturerSupportStats();
                setStats(response);
            } catch (error) {
                toast.error(t('ManufacturerSupport.errors.loadStats'));
            }
        };

        loadStats();
    }, [t]);

    return (
        <Wrapper>
            <Header>
                <div>
                    <Title>{t('ManufacturerSupport.title')}</Title>
                    <Subtitle>{t('ManufacturerSupport.subtitle')}</Subtitle>
                </div>
            </Header>
            <StatsGrid>
                <StatCard>
                    <label>{t('ManufacturerSupport.stats.open')}</label>
                    <strong>{stats?.open ?? '–'}</strong>
                </StatCard>
                <StatCard>
                    <label>{t('ManufacturerSupport.stats.resolved')}</label>
                    <strong>{stats?.resolved ?? '–'}</strong>
                </StatCard>
                <StatCard>
                    <label>{t('ManufacturerSupport.stats.avgResolution')}</label>
                    <strong>{stats?.avg_resolution_days?.toFixed(1) ?? '–'}</strong>
                    <small>{t('ManufacturerSupport.stats.days')}</small>
                </StatCard>
            </StatsGrid>

            <ClaimsTable />
        </Wrapper>
    );
};

const ClaimsTable = () => {
    const { t } = useTranslation();
    const [records, setRecords] = useState([]);
    const [pagination, setPagination] = useState({ current_page: 1, per_page: 10, total: 0 });
    const [filters, setFilters] = useState({ status: '', search: '' });
    const [loading, setLoading] = useState(false);
    const statusOptions = [
        { value: 'submitted', label: t('ManufacturerSupport.status.submitted') },
        { value: 'in_progress', label: t('ManufacturerSupport.status.inProgress') },
        { value: 'resolved', label: t('ManufacturerSupport.status.resolved') },
    ];

    const loadClaims = async (page = pagination.current_page, perPage = pagination.per_page, nextFilters = filters) => {
        setLoading(true);
        try {
            const response = await fetchManufacturerSupportClaims({
                page,
                perPage,
                status: nextFilters.status,
                search: nextFilters.search,
            });
            setRecords(response.data || []);
            setPagination({
                current_page: response.current_page,
                per_page: response.per_page,
                total: response.total,
            });
        } catch (error) {
            toast.error(t('ManufacturerSupport.errors.loadClaims'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadClaims(1, pagination.per_page, filters);
    }, [filters.status, filters.search]);

    const handleStatusChange = async (claim, status) => {
        try {
            await updateManufacturerSupportClaim(claim.id, { status });
            toast.success(t('ManufacturerSupport.notifications.statusUpdated'));
            loadClaims();
        } catch (error) {
            toast.error(t('ManufacturerSupport.errors.updateClaim'));
        }
    };

    const columns = [
        {
            name: t('ManufacturerSupport.table.claim'),
            selector: (row) => row.description?.slice(0, 60) || '—',
        },
        {
            name: t('ManufacturerSupport.table.item'),
            selector: (row) => row.item_name || '—',
        },
        {
            name: t('ManufacturerSupport.table.serial'),
            selector: (row) => row.serial_number || '—',
        },
        {
            name: t('ManufacturerSupport.table.claimDate'),
            selector: (row) => formatDate(row.claim_date),
        },
        {
            name: t('ManufacturerSupport.table.status'),
            cell: (row) => (
                <InlineSelect
                    value={row.status}
                    onChange={(event, nextValue) => handleStatusChange(row, nextValue ?? event?.target?.value)}
                    options={statusOptions}
                    inputId={`claim-status-${row.id}`}
                />
            ),
        },
    ];

    return (
        <section>
            <Filters>
                <FilterSelect
                    value={filters.status}
                    onChange={(event, nextValue) => setFilters({ ...filters, status: nextValue ?? event?.target?.value ?? '' })}
                    options={[
                        { value: '', label: t('ManufacturerSupport.filters.allStatuses') },
                        ...statusOptions,
                    ]}
                    inputId="manufacturer-support-filter-status"
                />
                <input
                    type="search"
                    value={filters.search}
                    placeholder={t('ManufacturerSupport.filters.searchPlaceholder')}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
            </Filters>
            <DataTable
                columns={columns}
                data={records}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={pagination.total}
                paginationPerPage={pagination.per_page}
                onChangePage={(page) => loadClaims(page, pagination.per_page)}
                onChangeRowsPerPage={(perPage, page) => loadClaims(page, perPage)}
                highlightOnHover
            />
        </section>
    );
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const Wrapper = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Title = styled.h1`
    margin: 0;
    color: rgba(130, 22, 146, 1);
`;

const Subtitle = styled.p`
    margin: 0.25rem 0 0 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
    margin: 1.5rem 0;
`;

const StatCard = styled.div`
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    padding: 1rem;
`;

const Filters = styled.div`
    display: flex;
    gap: 0.75rem;
    margin-bottom: 1rem;

    input {
        border: 1px solid rgba(130, 22, 146, 0.2);
        border-radius: 8px;
        padding: 0.5rem 0.625rem;
    }
`;

const FilterSelect = styled(SimpleSelect)`
    min-width: 220px;
`;

const InlineSelect = styled(SimpleSelect)`
    min-width: 170px;
`;

export default ManufacturerSupport;
